import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Avatar, Icon, Button, HStack, Badge } from "@chakra-ui/react";
import { FaStar, FaQuoteLeft, FaArrowRight, FaSolarPanel, FaHome, FaDollarSign } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { useNavigate } from "react-router-dom";

const SuccessStories: React.FC = () => {
  const navigate = useNavigate();

  const testimonials = [
    {
      name: "Sarah & Michael T.",
      location: "Brisbane, QLD",
      story: "We added solar to our investment property and within 3 months had quality tenants paying $50 more per week. The property value increased by $30,000 when we got it revalued!",
      category: "Solar + Property",
      icon: FaSolarPanel,
      rating: 5
    },
    {
      name: "James L.",
      location: "Sydney, NSW",
      story: "The finance team found us a rate 0.8% lower than our current mortgage and rolled in solar installation. Now we're saving $200/month on energy and $350/month on our mortgage!",
      category: "Finance + Solar",
      icon: FaDollarSign,
      rating: 5
    },
    {
      name: "Emma & David K.",
      location: "Melbourne, VIC",
      story: "As first home buyers, we thought solar was out of reach. One Group showed us how to finance it with our home loan. Now we're paying less overall than renting!",
      category: "First Home + Solar",
      icon: FaHome,
      rating: 5
    }
  ];

  const caseStudies = [
    {
      title: "Doubled ROI with Solar Property Investment",
      description: "How a Brisbane investor increased rental income by 15% and property value by $45,000 with strategic solar installation.",
      metrics: ["15% rent increase", "$45K value gain", "6-month payback"],
      category: "Property Investment"
    },
    {
      title: "From Renting to Owning with Solar Savings",
      description: "A young couple entered the property market and saved $3,500 annually with an integrated first home and solar package.",
      metrics: ["$3,500/year savings", "First home owner", "Zero upfront cost"],
      category: "First Home Buyer"
    },
    {
      title: "Refinance Success: Lower Rate + Solar Upgrade",
      description: "A family refinanced to a better rate and added solar, reducing combined costs by $600/month while future-proofing their home.",
      metrics: ["$600/month savings", "Better mortgage rate", "15kW solar system"],
      category: "Refinance + Solar"
    }
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/success-stories"
        fallbackTitle="Success Stories"
        fallbackDescription="Real stories from One Group clients who achieved their financial and sustainability goals"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              Success Stories
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Real Results from Real Australians
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              See how we've helped thousands of Australians achieve their solar, finance, and property goals
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Testimonial Cards */}
      <Container maxW="container.xl" py={20}>
        <Heading size="xl" textAlign="center" mb={12} color={brandConfig.colors.primary}>
          What Our Clients Say
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          {testimonials.map((testimonial, index) => (
            <Box
              key={index}
              p={8}
              bg="white"
              borderRadius="xl"
              boxShadow="xl"
              position="relative"
              transition="all 0.3s"
              _hover={{ transform: "translateY(-8px)", boxShadow: "2xl" }}
              borderTop="4px solid"
              borderColor={brandConfig.colors.primary}
            >
              <Icon
                as={FaQuoteLeft}
                w={8}
                h={8}
                color={brandConfig.colors.secondary}
                position="absolute"
                top={4}
                right={4}
                opacity={0.2}
              />
              <HStack spacing={4} mb={4}>
                <Avatar name={testimonial.name} size="md" bg={brandConfig.colors.primary} color="white" />
                <Box>
                  <Heading size="sm" color={brandConfig.colors.primary}>
                    {testimonial.name}
                  </Heading>
                  <Text fontSize="sm" color="gray.500">
                    {testimonial.location}
                  </Text>
                </Box>
              </HStack>

              <HStack spacing={1} mb={3}>
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Icon key={i} as={FaStar} color="#FBBF24" />
                ))}
              </HStack>

              <Text color="gray.600" mb={4} fontSize="sm" lineHeight="tall">
                "{testimonial.story}"
              </Text>

              <Badge
                colorScheme="blue"
                px={3}
                py={1}
                borderRadius="full"
                display="inline-flex"
                alignItems="center"
              >
                <Icon as={testimonial.icon} mr={1} />
                {testimonial.category}
              </Badge>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Case Study Highlights */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <Heading size="xl" textAlign="center" mb={12} color={brandConfig.colors.primary}>
            Case Study Highlights
          </Heading>
          <VStack spacing={8}>
            {caseStudies.map((study, index) => (
              <Box
                key={index}
                p={8}
                bg="white"
                borderRadius="lg"
                boxShadow="lg"
                w="full"
                transition="all 0.3s"
                _hover={{ boxShadow: "xl" }}
              >
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                  <Box gridColumn={{ base: "1", md: "1 / 3" }}>
                    <Badge colorScheme="green" mb={3}>
                      {study.category}
                    </Badge>
                    <Heading size="md" mb={3} color={brandConfig.colors.primary}>
                      {study.title}
                    </Heading>
                    <Text color="gray.600" mb={4}>
                      {study.description}
                    </Text>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="blue"
                      rightIcon={<Icon as={FaArrowRight} />}
                      onClick={() => navigate("/contact")}
                    >
                      Get Similar Results
                    </Button>
                  </Box>

                  <Box>
                    <Heading size="sm" mb={4} color={brandConfig.colors.primary}>
                      Key Metrics
                    </Heading>
                    <VStack align="start" spacing={2}>
                      {study.metrics.map((metric, idx) => (
                        <HStack key={idx}>
                          <Icon as={FaStar} color="#30D158" />
                          <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                            {metric}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </SimpleGrid>
              </Box>
            ))}
          </VStack>
        </Container>
      </Box>

      {/* Social Proof Stats */}
      <Container maxW="container.xl" py={20}>
        <VStack spacing={8} mb={12}>
          <Heading size="xl" textAlign="center" color={brandConfig.colors.primary}>
            Trusted by Thousands of Australians
          </Heading>
          <Text fontSize="lg" textAlign="center" maxW="3xl" color="gray.600">
            Join the growing community of satisfied clients who have transformed their financial future
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
          <Box textAlign="center" p={6} bg="gray.50" borderRadius="lg">
            <Heading size="2xl" color={brandConfig.colors.primary} mb={2}>
              5,000+
            </Heading>
            <Text color="gray.600">Happy Clients</Text>
          </Box>
          <Box textAlign="center" p={6} bg="gray.50" borderRadius="lg">
            <Heading size="2xl" color={brandConfig.colors.primary} mb={2}>
              4.9/5
            </Heading>
            <Text color="gray.600">Average Rating</Text>
          </Box>
          <Box textAlign="center" p={6} bg="gray.50" borderRadius="lg">
            <Heading size="2xl" color={brandConfig.colors.primary} mb={2}>
              $45M+
            </Heading>
            <Text color="gray.600">Client Savings</Text>
          </Box>
          <Box textAlign="center" p={6} bg="gray.50" borderRadius="lg">
            <Heading size="2xl" color={brandConfig.colors.primary} mb={2}>
              95%
            </Heading>
            <Text color="gray.600">Referral Rate</Text>
          </Box>
        </SimpleGrid>
      </Container>

      {/* CTA Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl">
              Ready to Write Your Success Story?
            </Heading>
            <Text fontSize="lg" maxW="2xl" color="rgba(255, 255, 255, 0.9)">
              Join thousands of satisfied clients who have achieved their solar, finance, and property goals with One Group.
            </Text>
            <Button
              size="lg"
              bg="white"
              color={brandConfig.colors.primary}
              _hover={{ bg: "gray.100", transform: "translateY(-2px)" }}
              rightIcon={<Icon as={FaArrowRight} />}
              onClick={() => navigate("/contact")}
              transition="all 0.2s"
            >
              Start Your Journey Today
            </Button>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default SuccessStories;
